import { Icon } from '@vp/swan';
import { DS_BRIEF_CREATE_URL } from '@99designs/i18n';
import { ExclusiveDesignIcon, SimpleDesignIcon, SimpleDesignIconExperiment } from '../icons';
import { Features, baseFeatures } from './featuresConfig';
import { PRICES, Price } from './priceConfig';
import { MpvId } from './productConfig';

export const TIER_IDS = ['simpleDesign', 'exclusiveDesign'] as const;
export const BASE_TIER = 'simpleDesign';
export type TierId = (typeof TIER_IDS)[number];
export type TierLocale = 'en-AU' | 'en-US';

export type Tier = {
    id: TierId;
    icon: React.ReactNode;
    title: string;
    description: string;
    price: Price;
    features: Features;
    briefURL: string;
    mobileDescription: string;
};

const createBaseTiers = (
    mpvId: MpvId,
    locale: TierLocale,
    overrides?: {
        exclusiveDesign?: Partial<Pick<Tier, 'briefURL'>>;
    }
) => {
    const getPrice = (tierId: TierId, locale: TierLocale): Price => {
        const price = PRICES[mpvId]?.[tierId]?.[locale];

        if (!price) {
            throw new Error(
                `Price not found for mpvId: ${mpvId}, tierId: ${tierId}, locale: ${locale}`
            );
        }

        return price;
    };

    const simpleDesignTier: Tier = {
        id: 'simpleDesign',
        icon: <SimpleDesignIcon />,
        title: 'Simple Design',
        description: 'When you need a professional design, fast.',
        price: getPrice('simpleDesign', locale),
        briefURL: `${DS_BRIEF_CREATE_URL}/care-design?mpvId=${mpvId}&requestType=Standard`,
        features: baseFeatures,
        mobileDescription:
            'One of our available designers creates a simple design, fast. This team is trained to quickly deliver high-quality designs for immediate printing – perfect for straightforward, streamlined projects.',
    };

    const exclusiveDesignTier: Tier = {
        id: 'exclusiveDesign',
        icon: <ExclusiveDesignIcon />,
        title: 'Exclusive Design',
        description: 'A custom design created from scratch.',
        price: getPrice('exclusiveDesign', locale),
        briefURL: overrides?.exclusiveDesign?.briefURL || '',
        mobileDescription:
            'Get a custom, handcrafted design created from scratch by a designer. Browse portfolios, choose a designer whose style suits your needs and work closely with them to bring your vision to life.',
        features: {
            ...baseFeatures,
            suitableFor: {
                ...baseFeatures.suitableFor,
                description: {
                    text: 'Unique and personalized designs',
                },
            },
            turnaroundTime: {
                ...baseFeatures.turnaroundTime,
                description: {
                    text: '3-day turnaround',
                },
                mobileDescription:
                    '3-day design turnaround time (excludes weekends and public holidays)',
            },
            matchingDesignDetails: {
                ...baseFeatures.matchingDesignDetails,
                description: { text: 'Choose your designer' },
                icon: <Icon iconType="consumer" size="standard" skin="subtle" />,
            },
            revisions: {
                ...baseFeatures.revisions,
                description: { text: 'rounds of revisions', amount: 5 },
                mobileDescription: '5 rounds of revisions to use within 30 days',
            },
            designerMatching: {
                ...baseFeatures.designerMatching,
                description: {
                    text: 'Choose your favorite designer',
                },
            },
            messaging: {
                ...baseFeatures.messaging,
                mobileDescription: 'Messaging',
            },
        },
    };

    return {
        simpleDesignTier,
        exclusiveDesignTier,
    };
};

const createExperimentVariants = (baseTiers: ReturnType<typeof createBaseTiers>) => {
    const { simpleDesignTier } = baseTiers;

    const simpleDesignThreeDayVariant: Tier = {
        ...simpleDesignTier,
        icon: <SimpleDesignIconExperiment />,
        description: 'Professional designs at an affordable price.',
        mobileDescription:
            'One of our available designers creates a simple design. This team is trained to quickly deliver high-quality designs for immediate printing – perfect for straightforward, streamlined projects.',
        features: {
            ...baseFeatures,
            turnaroundTime: {
                ...baseFeatures.turnaroundTime,
                description: {
                    text: '3-day turnaround',
                },
                mobileDescription:
                    '3-day design turnaround time (excludes weekends and public holidays)',
            },
        },
    };

    return {
        simpleDesignThreeDayVariant,
    };
};

export const generateTiers = (
    mpvId: MpvId,
    locale: TierLocale,
    options?: {
        useExperimentVariants?: boolean;
        overrides?: {
            exclusiveDesign?: Partial<Pick<Tier, 'briefURL'>>;
        };
    }
): Tier[] => {
    const baseTiers = createBaseTiers(mpvId, locale, options?.overrides);

    if (options?.useExperimentVariants) {
        const experimentVariants = createExperimentVariants(baseTiers);
        return [
            experimentVariants.simpleDesignThreeDayVariant,
            // We are not changing the exclusive design tier in the experiment variants
            baseTiers.exclusiveDesignTier,
        ];
    }

    return [baseTiers.simpleDesignTier, baseTiers.exclusiveDesignTier];
};
